import React, { useState } from 'react'
import { 
  Box, 
  Typography,
  Button
} from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  updateProductQty,
  removeProduct,
  resetDP
} from './reducer/DpActions'
import {
  selectCurrentCart,
  selectCurrentCartTotals,
  selectOrderBookingId,
  selectSelectedShipToPartyId,
  selectShipToPartyCarts,
  selectPoOpenQty,
  selectTotalDPQuantity,
  selectMOQ
} from './reducer/DpSelectors'
import ProductCard from '../../Inventory/components/ProductCard'
import { CustomButton } from '../../../v2/MUI_custom_component'
import { DP_ROUTE_SOURCE } from './Enums'
import { ModifyOrderAction } from './API/Action'
import { toast } from 'react-toastify'
import { OperationType } from '../../../utils/settings'
import DisclaimerSvg from '../../Illustrration/components/DisclaimerSvg'
import HumbeeModal from '../../../v2/MUI_custom_component/Modal/HumbeeModal'

const DPCart = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation();
    const selectedShipToPartyId = useSelector(selectSelectedShipToPartyId)
    const shipToPartyCarts = useSelector(selectShipToPartyCarts);
    const cartItems = useSelector(selectCurrentCart)
    const cartTotals = useSelector(selectCurrentCartTotals)
    const poOpenQty = useSelector(selectPoOpenQty)
    const totalDPQuantity = useSelector(selectTotalDPQuantity)
    const selectMOQData = useSelector(selectMOQ)
    const source = location.state?.from;
    const [showUpdateOrderModal, setshowUpdateOrderModal] = useState(false)
    const [loading, setLoading] = useState(false)
    const displayCartItems = source === DP_ROUTE_SOURCE.ORDER_SUMMARY 
        ? cartItems.filter(item => {
            if (item.isExistingItem !== true) {
                return true;
            }
            if (item.isExistingItem && item.originalQuantity) {
                return item.quantity.quantity_in_metric_ton !== item.originalQuantity.quantity_in_metric_ton;
            }
            return false;
        })
        : cartItems;

    const { orderBookingId, order_id } = useSelector((state) => {
        return {
            order_id: state.dpReducer?.order_id,
            order_booking_id: state.dpReducer?.order_booking_id,
        };
    });
    const roundToThreeDecimals = (num) => {
        return Math.round((num + Number.EPSILON) * 1000) / 1000
    }

    const getProductQuantity = (productId) => {
        const product = cartItems.find(p => p.product_id === productId)
        if (product) {
            return product.quantity.quantity_in_metric_ton
        }
        
        for (const stpCart of Object.values(shipToPartyCarts)) {
            const existingProduct = stpCart.cart?.find(item => item.product_id === productId && item.isExistingItem)
            if (existingProduct) {
                return existingProduct.quantity.quantity_in_metric_ton
            }
        }
        
        return 0
    }

    const getMaxQuantityForProduct = (productId) => {
        if (!poOpenQty || poOpenQty <= 0) {
            return 0
        }
        
        const currentQty = getProductQuantity(productId)
        const remainingPOQty = Math.max(0, poOpenQty - totalDPQuantity)
        
        let maxAllowableQty
        if (selectMOQData?.max_order_quantity && typeof selectMOQData.max_order_quantity === 'number' && selectMOQData.max_order_quantity > 0) {
            const remainingDPQty = Math.max(0, selectMOQData.max_order_quantity - totalDPQuantity)
            maxAllowableQty = Math.min(remainingPOQty, remainingDPQty)
        } else {
            maxAllowableQty = remainingPOQty
        }
        
        return roundToThreeDecimals(currentQty + maxAllowableQty)
    }

    const validatePOLimit = (productId, newQty) => {
        if (!poOpenQty || poOpenQty <= 0) {
            toast.error('No PO open quantity limit available')
            return false
        }

        const currentQty = getProductQuantity(productId)
        const qtyIncrease = newQty - currentQty
        
        if (qtyIncrease <= 0) {
            return true
        }

        const newTotalQty = totalDPQuantity + qtyIncrease
        const remainingPOQty = Math.max(0, poOpenQty - totalDPQuantity)
        const remainingDPQty = (selectMOQData?.max_order_quantity && typeof selectMOQData.max_order_quantity === 'number' && selectMOQData.max_order_quantity > 0) 
            ? Math.max(0, selectMOQData.max_order_quantity - totalDPQuantity) 
            : Infinity
        
        const isPOLimitExceeded = newTotalQty > poOpenQty
        const isDPLimitExceeded = (selectMOQData?.max_order_quantity && typeof selectMOQData.max_order_quantity === 'number' && selectMOQData.max_order_quantity > 0) 
            && newTotalQty > selectMOQData.max_order_quantity
        
        if (isPOLimitExceeded || isDPLimitExceeded) {
            const maxAllowedPO = roundToThreeDecimals(currentQty + remainingPOQty)
            const maxAllowedDP = (selectMOQData?.max_order_quantity && typeof selectMOQData.max_order_quantity === 'number' && selectMOQData.max_order_quantity > 0) 
                ? roundToThreeDecimals(currentQty + remainingDPQty) 
                : Infinity
            
            const finalMaxAllowed = Math.min(maxAllowedPO, maxAllowedDP)
            
            if (finalMaxAllowed <= currentQty) {
                if (remainingDPQty <= remainingPOQty) {
                    toast.error(`DP quantity limit of ${roundToThreeDecimals(selectMOQData.max_order_quantity)} has been reached. Cannot add more items.`)
                } else {
                    toast.error(`PO quantity limit of ${roundToThreeDecimals(poOpenQty)} has been reached. Cannot add more items.`)
                }
                return false
            } else {
                if (remainingDPQty <= remainingPOQty) {
                    toast.error(`DP Limit reached. Maximum allowed for this item: ${finalMaxAllowed} ton`)
                } else {
                    toast.error(`PO Limit reached. Maximum allowed for this item: ${finalMaxAllowed} ton`)
                }
                return finalMaxAllowed
            }
        }
        
        return true
    }

    
    const handleQuantityIncrease = (productId) => {
        let currentProduct = cartItems.find(p => p.product_id === productId)
        let targetShipToPartyId = selectedShipToPartyId
        
        if (!currentProduct) {
            for (const [stpId, stpCart] of Object.entries(shipToPartyCarts)) {
                const existingProduct = stpCart.cart?.find(item => item.product_id === productId && item.isExistingItem)
                if (existingProduct) {
                    currentProduct = existingProduct
                    targetShipToPartyId = stpId
                    break
                }
            }
        }
        
        if (currentProduct) {
            const newQuantity = currentProduct.quantity.quantity_in_metric_ton + 1
            const validation = validatePOLimit(productId, newQuantity)
            if (validation === false) {
                return
            }
            const finalQty = typeof validation === 'number' ? validation : newQuantity
            dispatch(updateProductQty(productId, finalQty, targetShipToPartyId))
        }
    }
    const { selectedEntity: selectedCompanyEntity } = useSelector(
        (state) => state.entityReducer
    );
    const handleQuantityDecrease = (productId) => {
        let currentProduct = cartItems.find(p => p.product_id === productId)
        let targetShipToPartyId = selectedShipToPartyId
        if (!currentProduct) {
            for (const [stpId, stpCart] of Object.entries(shipToPartyCarts)) {
                const existingProduct = stpCart.cart?.find(item => item.product_id === productId && item.isExistingItem)
                if (existingProduct) {
                    currentProduct = existingProduct
                    targetShipToPartyId = stpId
                    break
                }
            }
        }
        if (currentProduct) {
            const newQuantity = currentProduct.quantity.quantity_in_metric_ton - 1
            if (newQuantity <= 0) {
                dispatch(removeProduct(productId, targetShipToPartyId))
            } else {
                dispatch(updateProductQty(productId, newQuantity, targetShipToPartyId))
            }
        }
    }
    const addProducts = async () => {
        const payload = {
            order_id: order_id,
            order_products_delivery: Object.values(shipToPartyCarts)
            .map((stp) => {
                const d = stp.shipToPartyDetails;
                const productsToProcess = stp.cart;
                if (productsToProcess.length === 0) return null;

                return {
                ship_to_party_info: {
                    so_id: d?.humbee_so_id,
                    operation_type: null,
                    deliver_to: d?.company_id,
                    deliver_to_esi_id: d?.selected_esi_id,
                    requested_district_id: d?.delivery_district_id,
                    pricing_geography_id: d?.pricing_geography_id,
                },

                products_info: productsToProcess.map((item) => {
                    let finalQuantityInMetricTon = item.quantity.quantity_in_metric_ton;
                    
                    if (item.isExistingItem && item.originalQuantity) {
                        const currentCartQuantity = item.quantity.quantity_in_metric_ton;
                        const originalQuantity = item.originalQuantity.quantity_in_metric_ton;
                        if (currentCartQuantity !== originalQuantity) {
                            finalQuantityInMetricTon = originalQuantity + currentCartQuantity;
                        } else {
                            finalQuantityInMetricTon = originalQuantity;
                        }
                    }
                    
                    return {
                        order_product_delivery_id: item.order_product_delivery_id || null,
                        operation_type: item.isExistingItem ? OperationType['UPDATE'] : OperationType['ADD'],
                        product_id: item.product_id,

                        quantity: {
                            quantity_in_bags: item.isExistingItem && item.originalQuantity && finalQuantityInMetricTon !== item.quantity.quantity_in_metric_ton
                                ? Math.round(finalQuantityInMetricTon * item.quantity.multiplier)
                                : item.quantity.quantity_in_bags,
                            quantity_in_metric_ton: finalQuantityInMetricTon,
                            multiplier: item.quantity.multiplier,
                            unit: item.quantity.unit,
                            base_unit: item.quantity.base_unit
                        },

                        form_type: item.form_type,
                        bundle: item.bundle || 0,
                        length: item.length || '',
                        remark: item.remark || ''
                    };
                })
                };
            })
            .filter(Boolean)
        };

        setLoading(true)
        const { error, message } = await ModifyOrderAction(payload, selectedCompanyEntity);
        setLoading(false)
        
        if (!error) {
            toast.success(message)
            dispatch(resetDP())
            navigate(`/dispatch-plan-detail/${order_id}`);
        }else{
            toast.error(message)
        }
    };
    const handleRemoveItem = (productId) => {
        const currentProduct = cartItems.find(p => p.product_id === productId)
        if (currentProduct) {
            dispatch(removeProduct(productId, selectedShipToPartyId))
            return
        }
        
        for (const [stpId, stpCart] of Object.entries(shipToPartyCarts)) {
            const existingProduct = stpCart.cart?.find(item => item.product_id === productId && item.isExistingItem)
            if (existingProduct) {
                dispatch(removeProduct(productId, stpId))
                break
            }
        }
    }
    const { order_booking_id } = useSelector((state) => {
        return {
            order_booking_id: state.dpReducer?.order_booking_id,
        };
    });

    const handleContinue = () => {
        if (displayCartItems.length === 0) {
            navigate('/create-dp');
            return;
        }
        if (source === DP_ROUTE_SOURCE.ORDER_SUMMARY) {
            navigate('/dp-order-summary');
            return;
        }
        if (source === DP_ROUTE_SOURCE.DP_DETAILS) {
            setshowUpdateOrderModal(true)
            return;
        }
        navigate('/dp-ship-to-party-selection', {
            state: {
                order_id,
                orderBookingId,
                selectedShipToPartyId,
            },
        });
    };

    const EmptyCartComponent = () => {
        return (
            <Box
                sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 5,
                textAlign: 'center',
                }}
            >
                <img
                    src='https://d2djimvzddgguw.cloudfront.net/humbee-inventory/cart.png'
                    alt='Cart Empty'
                    style={{ width: '100%', maxWidth: 180, marginBottom: 20 }}
                />
                <Typography variant="h2" sx={{ mb: 2}}>
                    Cart Empty!
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                    Add products to cart to proceed.
                </Typography>
                <CustomButton
                    variant="filled"
                    color="primary"
                    label="Add Products"
                    onClick={() => {
                        navigate('/create-dp')
                    }}
                    sx={{ width: { xs: '80%', sm: 'auto' } }}
                />
            </Box>
        );
    };
    

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent:'end', 
                p: { xs: 2, md: 0 },
                mr: { xs: 0, md: 4 }, 
                position: {xs: 'sticky', md: 'relative'}, 
                top: 0, 
                zIndex: 1000, 
                boxShadow: {xs: '0px 2px 8px 0px #0000001A', md: 'none'},
                background: {xs: '#FAFAFA', md: 'transparent'}
            }}>
                <CustomButton
                    variant="filled"
                    color="primary"
                    disabled={displayCartItems.length === 0}
                    label="Continue"
                    onClick={handleContinue}
                    sx={{ width : { xs: '100%', sm: 'auto' } }}
                />
            </Box>
            <Box sx={{ p: { xs: 2, sm: 4 } }}>
            
            {displayCartItems.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <Typography variant="h2">
                            Cart
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between'}}>
                        <Typography variant="labelD">
                            Total Quantity: 
                        </Typography>
                        <Typography variant="labelD">
                            {displayCartItems.reduce((sum, item) => sum + item.quantity.quantity_in_metric_ton, 0)} {displayCartItems[0]?.quantity?.unit || 'Tons'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="labelD">
                            UOM: 
                        </Typography>
                        <Typography variant="labelD">
                            {displayCartItems[0]?.quantity?.unit || 'Tons'}
                        </Typography>
                    </Box>
                </Box>
            )}
            <Box>
                {displayCartItems.length === 0 ? (
                <EmptyCartComponent />
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {displayCartItems.map((item) => (
                        <ProductCard
                            key={item.product_id}
                            productName={item.product_name}
                            imageUrl={item.product_image || '/placeholder-image.png'}
                            price={item.current_price_per_unit}
                            quantity={item.quantity.quantity_in_metric_ton}
                            maxQuantity={getMaxQuantityForProduct(item.product_id)}
                            showQuantityControls={true}
                            showDeleteButton={true}
                            onIncreasePress={() => {
                                handleQuantityIncrease(item.product_id)
                            }}
                            onReducePress={() => {
                                handleQuantityDecrease(item.product_id)
                            }}
                            onTextChange={(event) => {
                                const input = event.target ? event.target.value : event
                                const newQty = parseFloat(input) || 0
                                if (newQty === 0) {
                                    handleRemoveItem(item.product_id)
                                } else {
                                    const validation = validatePOLimit(item.product_id, newQty)
                                    if (validation !== false) {
                                        const finalQty = typeof validation === 'number' ? validation : newQty
                                        
                                        let targetShipToPartyId = selectedShipToPartyId
                                        const currentProduct = cartItems.find(p => p.product_id === item.product_id)
                                        
                                        if (!currentProduct) {
                                            for (const [stpId, stpCart] of Object.entries(shipToPartyCarts)) {
                                                const existingProduct = stpCart.cart?.find(cartItem => cartItem.product_id === item.product_id && cartItem.isExistingItem)
                                                if (existingProduct) {
                                                    targetShipToPartyId = stpId
                                                    break
                                                }
                                            }
                                        }
                                        
                                        dispatch(updateProductQty(item.product_id, finalQty, targetShipToPartyId))
                                    }
                                }
                            }}
                            onDelete={() => {
                                handleRemoveItem(item.product_id)
                            }}
                            row={{ 
                                product_info: { 
                                    product_id: item.product_id,
                                    product_name: item.product_name 
                                } 
                            }}
                        />
                    ))}
                    </Box>
                )}
            </Box>
            </Box>
            <HumbeeModal
                open={showUpdateOrderModal}
                onClose={() => setshowUpdateOrderModal(false)}
                onConfirm={addProducts}
                loading={loading}
                title='New items are added to your DP'
                subtitle='Your order will be updated.'
                confirmLabel = "Update Order"
                cancelLabel = "No"
                illustration={<DisclaimerSvg />}
                width={{ xs: 400 , md:  400 }}
            />
        </>
    )
    }

export default DPCart